import type { Tokens } from 'marked'
import { marked } from 'marked'
import { markedSmartypants } from 'marked-smartypants'
import { useEffect, useState } from 'react'
import { getRegionByCode, getRegionCodes } from '@luxuryescapes/lib-regions'
import config from 'constants/config'
import DOMPurify from 'isomorphic-dompurify'

// This library contains a custom link renderer intended for use with marked
// markdown rendering library. It adds the nofollow attribute to all anchors,
// with the exception of the following domains.
//
// Offer content sometimes contains links to external pages. We want these
// links to be only followed by Google when it is beneficial to LE.com.  We
// introduce a “safelist” for domains that are allowed to be followed.  The
// default behaviour should be to introduce a “nofollow” attribute to links
// that are not in this list.

const whitelist = [
  'facebook.com',
  'apple.com',
  'twitter.com',
  'play.google.com',
  'instagram.com',
  'youtube.com',
  'pinterest.com',
  'cudo.com.au',
  'deals.com.au',
  'treatme.co.nz',
  'luxuryescapes.com',
]

const TRIP_PLANNER_OFFER_LINK_PROTOCOLS = new Set(['hotel', 'experience', 'cruise'])

function whitelisted(url: string): boolean {
  return whitelist.some(domain => url.startsWith(`https://${domain}`) ||
    url.startsWith(`http://${domain}`) ||
    url.startsWith(`https://www.${domain}`) ||
    url.startsWith(`http://www.${domain}`))
}

export function markdownLinkRenderer(link: Tokens.Link): string {
  const { href, title, text } = link

  // Trip Planner: Google Places locations (resolved place ID)
  if (href.startsWith('place:')) {
    const placeId = href.slice('place:'.length)
    const escapedPlaceId = DOMPurify.sanitize(placeId, { ALLOWED_TAGS: [] }).replace(/"/g, '&quot;')
    const escapedText = DOMPurify.sanitize(text, { ALLOWED_TAGS: [] }).replace(/"/g, '&quot;')
    // Intentionally not keyboard-accessible — place tags are mouse-only interactions
    // scoped to Trip Planner chat. Keyboard support is tracked as a future a11y improvement.
    return `<span class="place-tag" data-place-id="${escapedPlaceId}" data-place-name="${escapedText}">${escapedText}</span>`
  }

  // Trip Planner: Place search queries (need resolution before opening drawer)
  if (href.startsWith('place-search:')) {
    const raw = href.slice('place-search:'.length)
    let searchQuery: string
    try {
      searchQuery = decodeURIComponent(raw).trim()
    } catch {
      searchQuery = raw.trim()
    }
    const escapedQuery = DOMPurify.sanitize(searchQuery, { ALLOWED_TAGS: [] }).replace(/"/g, '&quot;')
    const escapedText = DOMPurify.sanitize(text, { ALLOWED_TAGS: [] }).replace(/"/g, '&quot;')
    // Intentionally not keyboard-accessible — place tags are mouse-only interactions
    // scoped to Trip Planner chat. Keyboard support is tracked as a future a11y improvement.
    return `<span class="place-tag" data-place-search="${escapedQuery}" data-place-name="${escapedText}">${escapedText}</span>`
  }

  // Trip Planner: LE offer links — hotel:offerId, experience:offerId, or cruise:offerId
  // These are persisted in message content and rendered as clickable spans
  // that open the offer detail drawer via the open_offer_drawer event bus.
  // DrawerContainer branches on data-offer-type to pick the right drawer component.
  const [protocol, ...rest] = href.split(':')
  if (TRIP_PLANNER_OFFER_LINK_PROTOCOLS.has(protocol) && rest.length > 0) {
    const offerType = protocol
    const offerId = rest.join(':')
    const escapedOfferId = DOMPurify.sanitize(offerId, { ALLOWED_TAGS: [] }).replace(/"/g, '&quot;')
    const escapedText = DOMPurify.sanitize(text, { ALLOWED_TAGS: [] }).replace(/"/g, '&quot;')
    return `<span class="offer-tag" data-offer-id="${escapedOfferId}" data-offer-type="${offerType}" data-offer-name="${escapedText}" role="button" tabindex="0">${escapedText}</span>`
  }

  let rel = 'noopener noreferrer'
  if (!whitelisted(href)) {
    rel += ' nofollow'
  }
  let out = `<a target="_blank" rel="${rel}" href="${href}"`
  if (title) {
    out += ` title="${title}"`
  }
  out += `>${text}</a>`
  return out
}

marked.use({
  gfm: true,
  breaks: false,
  pedantic: false,
  renderer: {
    link: markdownLinkRenderer,
  },
})

marked.use(markedSmartypants() as any)

// DOMPurify configuration for markdown sanitization
// Security: DOMPurify's default URI handling blocks javascript:, vbscript:, data: URIs
// Note: 'rel' and 'loading' are already in DOMPurify's default allowed attributes
const sanitiseConfig = {
  // Required: 'target' is not in DOMPurify's defaults, needed for target="_blank"
  ADD_ATTR: ['target'],
  // Security: Block form elements to prevent phishing in CMS content
  FORBID_TAGS: ['form', 'input', 'button', 'select', 'textarea'],
}

// Used to escape numbers with a + prefixing them
function escapeRegExp(string: string) {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

export function phoneLinksDecorator(Component: any) {
  return function WrappedComponent(props: any) {
    const [processedMessage, setProcessedMessage] = useState(props.message?.text ?? '')

    useEffect(() => {
      let newText = props.message?.text ?? ''

      /*
        Get all regions for brand then loop through these regions and create set from all numbers.
        Later discuss implementing new function into lib-regions that gets all numbers regardless of region.
      */

      const regionContacts = getRegionCodes(config.BRAND)
        .flatMap(region => getRegionByCode(region, config.BRAND)?.contacts || [])

      const uniqueNumbers = new Set<string>()

      regionContacts?.forEach(item => {
        if (props.message?.text?.includes(item.international.humanReadable || item.international.number)) {
          uniqueNumbers.add(item.international.humanReadable)
        }
        if (props.message?.text?.includes(item.local.humanReadable || item.local.number)) {
          uniqueNumbers.add(item.local.humanReadable)
        }
      })

      uniqueNumbers.forEach(number => {
        const escapedNumber = escapeRegExp(number)
        const regex = new RegExp(`(${escapedNumber})`, 'g')
        newText = newText.replace(regex, `<a href="tel:${number}">${number}</a>`)
      })

      setProcessedMessage(newText)
    }, [props.message])

    return <Component {...props} processedMessage={processedMessage} />
  }
}

export function renderMarkdown(content: string): string {
  const html = marked.parse(content, { async: false })
  return DOMPurify.sanitize(html, sanitiseConfig)
}

/** Inline campaign copy: bold, italic, and underline, without a paragraph wrapper. */
export function renderInlineMarkdown(content: string): string {
  const html = marked.parseInline(content, { async: false, gfm: true, breaks: false })
  return DOMPurify.sanitize(html, sanitiseConfig)
}
